import type { GenerationRunRead } from '../types/content.types';

import type { QualitySection } from './quality-sections';
import { QUALITY_SECTIONS } from './quality-sections';
import { runPurpose, runScores, runUnderstands, runWrites } from './run-summary';

/**
 * Every section the Job page can show. A job shows only those it is about —
 * see `runTabsFor`. On a scoring job the Quality report's sections are tabs of their own.
 */
export const RUN_TABS = [
  'content',
  'quality',
  ...QUALITY_SECTIONS,
  'attribution',
  'problems',
  'before-after',
  'technical',
] as const;

export type RunTab = (typeof RUN_TABS)[number];

/**
 * Tab values from before the Job page redesign. Still accepted in `?tab=` so
 * bookmarks and links elsewhere keep landing on the matching section.
 * `results` was folded into the status card and now opens the job's first tab.
 * (`overview`, once legacy too, is a scoring job's first tab again; on any other
 * job it isn't in the list, so `pickRunTab` sends it to the first tab as before.)
 */
export const LEGACY_RUN_TABS = [
  'results',
  'failures',
  'gap-analysis',
  'batches',
  'workflow',
] as const;

export type LegacyRunTab = (typeof LEGACY_RUN_TABS)[number];

/** Everything the route's `tab` search param accepts: current + legacy values. */
export const RUN_TAB_PARAMS = [...RUN_TABS, ...LEGACY_RUN_TABS] as const;

export type RunTabParam = (typeof RUN_TAB_PARAMS)[number];

/** The two views inside "Technical details". */
export const RUN_TECH_VIEWS = ['batches', 'engine'] as const;

export type RunTechView = (typeof RUN_TECH_VIEWS)[number];

/**
 * Where a legacy tab value now lives: the new tab plus, for support views, which
 * sub-view. `null` means "the job's own first tab", which needs the loaded job to pick.
 */
const LEGACY_MAP: Record<LegacyRunTab, { tab: RunTab | null; tech?: RunTechView }> = {
  results: { tab: null },
  failures: { tab: 'problems' },
  'gap-analysis': { tab: 'before-after' },
  batches: { tab: 'technical', tech: 'batches' },
  workflow: { tab: 'technical', tech: 'engine' },
};

/**
 * Resolves the URL's `tab` (+ optional `tech`) into the section asked for, so old
 * links like `?tab=workflow` open Technical details › Engine instead of 404-ing.
 * `tab: null` (no tab, or `results`) defers to `pickRunTab` once the job loads.
 */
export function resolveRunTab(
  tab: RunTabParam | undefined,
  tech: RunTechView | undefined,
): { tab: RunTab | null; tech: RunTechView } {
  if (!tab) return { tab: null, tech: tech ?? 'batches' };
  if ((LEGACY_RUN_TABS as readonly string[]).includes(tab)) {
    const mapped = LEGACY_MAP[tab as LegacyRunTab];
    return { tab: mapped.tab, tech: mapped.tech ?? tech ?? 'batches' };
  }
  return { tab: tab as RunTab, tech: tech ?? 'batches' };
}

/** Whether a tab is one of the Quality report's sections (a scoring job's own tabs). */
export function isQualitySectionTab(tab: RunTab | null): tab is QualitySection {
  return tab !== null && (QUALITY_SECTIONS as readonly string[]).includes(tab);
}

/**
 * The tabs one job shows, in order — only what it is about, support last.
 * `showProblems` is true while the job runs or once it has failed items, so a clean
 * job doesn't offer an empty Problems tab.
 */
export function runTabsFor(run: Pick<GenerationRunRead, 'flows'>, showProblems: boolean): RunTab[] {
  const problems: RunTab[] = showProblems ? ['problems'] : [];
  switch (runPurpose(run)) {
    case 'score':
      return [...QUALITY_SECTIONS, ...problems, 'technical'];
    case 'write':
      return ['content', ...problems, 'before-after', 'technical'];
    case 'understand':
      return ['attribution', ...problems, 'technical'];
    case 'custom': {
      // A custom job mixes steps — each section shows only if one of its steps ran.
      const writes = runWrites(run);
      const tabs: RunTab[] = [];
      if (writes) tabs.push('content');
      if (runScores(run)) tabs.push('quality');
      if (runUnderstands(run)) tabs.push('attribution');
      tabs.push(...problems);
      if (writes) tabs.push('before-after');
      tabs.push('technical');
      return tabs;
    }
  }
}

/**
 * The tab to open. A requested tab the job shows wins; one it doesn't (an old link)
 * goes to the closest it does — `quality` ↔ the scoring sections — else the first tab.
 * With nothing usable asked for, a writing job that wrote nothing but has
 * problems (`preferProblems`) opens Problems; anything else opens its first tab.
 */
export function pickRunTab(
  requested: RunTab | null,
  tabs: readonly RunTab[],
  preferProblems: boolean,
): RunTab {
  if (requested && tabs.includes(requested)) return requested;
  if (requested === 'quality' && tabs.includes('overview')) return 'overview';
  if (isQualitySectionTab(requested) && tabs.includes('quality')) return 'quality';
  const first = tabs[0] ?? 'technical';
  return first === 'content' && preferProblems && tabs.includes('problems') ? 'problems' : first;
}
